/* AdminDeliveryPartners.jsx */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { assignDeliveryPartnerRole, revokeDeliveryPartnerRole } from "../../utils/adminFunctions";

const AdminDeliveryPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [approving, setApproving] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const partnersRef = collection(db, "users");
        let q = query(partnersRef, where("role", "==", "deliveryPartner"));
        const snapshot = await getDocs(q);
        let data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        if (filter !== "all") {
          data = data.filter((partner) => partner.status === filter);
        }
        setPartners(data);
      } catch (error) {
        console.error("Error fetching partners:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, [filter]);

  // Approve handler
  const handleApprove = async (uid) => {
    try {
      setApproving(true);
      await assignDeliveryPartnerRole(uid);
      // Update status in Firestore
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, { status: "approved" });
      alert("✅ Delivery Partner Approved Successfully!");
      // Refresh list
      const partnersRef = collection(db, "users");
      let q = query(partnersRef, where("role", "==", "deliveryPartner"));
      const snapshot = await getDocs(q);
      let data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      if (filter !== "all") {
        data = data.filter((partner) => partner.status === filter);
      }
      setPartners(data);
    } catch (err) {
      console.error("❌ Error assigning role:", err);
      alert("❌ Failed to approve: " + err.message);
    } finally {
      setApproving(false);
    }
  };

  // Revoke handler
  const handleRevoke = async (uid) => {
    try {
      setRevoking(true);
      await revokeDeliveryPartnerRole(uid);
      // Update status in Firestore
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, { status: "rejected" });
      alert("✅ Delivery Partner Role Revoked!");
      // Refresh list
      const partnersRef = collection(db, "users");
      let q = query(partnersRef, where("role", "==", "deliveryPartner"));
      const snapshot = await getDocs(q);
      let data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      if (filter !== "all") {
        data = data.filter((partner) => partner.status === filter);
      }
      setPartners(data);
    } catch (err) {
      console.error("❌ Error revoking role:", err);
      alert("❌ Failed to revoke: " + err.message);
    } finally {
      setRevoking(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: ["bg-green-100", "text-green-800", "✅ Approved"],
      pending: ["bg-yellow-100", "text-yellow-800", "⏳ Pending"],
      rejected: ["bg-red-100", "text-red-800", "❌ Rejected"],
    };
    const [bg, text, label] = badges[status] || [
      "bg-gray-100",
      "text-gray-800",
      "❓ Unknown",
    ];
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}
      >
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">
            Loading delivery partners...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">
                JR Home Made Foods
              </h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>
      <div className="p-6 max-w-7xl mx-auto">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="mb-4 inline-flex items-center px-4 py-2 bg-green-100 text-green-700 font-medium rounded-md hover:bg-green-200"
        >
          ← Back to Dashboard
        </button>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-green-700">
            🚚 Delivery Partners
          </h1>
          <div className="flex gap-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Partners</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {["Total", "Pending", "Approved", "Rejected"].map((label, i) => {
            const colors = [
              "text-green-700",
              "text-yellow-700",
              "text-green-700",
              "text-red-700",
            ];
            const filters = ["all", "pending", "approved", "rejected"];
            const count =
              filters[i] === "all"
                ? partners.length
                : partners.filter((p) => p.status === filters[i]).length;
            return (
              <div
                key={label}
                className="bg-white p-4 rounded-xl shadow-sm border border-green-100"
              >
                <div className="text-sm text-gray-600">{label} Partners</div>
                <div className={`text-2xl font-bold ${colors[i]}`}>
                  {count}
                </div>
              </div>
            );
          })}
        </div>

        {partners.length === 0 ? (
          <div className="bg-white p-8 rounded-xl shadow-sm border border-green-100 text-center">
            <div className="text-4xl mb-4">🚚</div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No Delivery Partners Found
            </h3>
            <p className="text-gray-600">
              {filter === "all"
                ? "No delivery partners have registered yet."
                : `No delivery partners with status "${filter}" found.`}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="bg-white p-6 rounded-xl shadow-sm border border-green-100"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-green-700">
                        {partner.name || "Unnamed Partner"}
                      </h3>
                      {getStatusBadge(partner.status)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-700">
                          <span className="font-medium">Email:</span>{" "}
                          {partner.email}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">Phone:</span>{" "}
                          {partner.phone || "N/A"}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">Area:</span>{" "}
                          {partner.area || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">
                          <span className="font-medium">Aadhaar:</span>{" "}
                          {partner.documents?.aadhaar || "N/A"}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">RC:</span>{" "}
                          {partner.documents?.rc || "N/A"}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-medium">DL:</span>{" "}
                          {partner.documents?.dl || "N/A"}
                        </p>
                      </div>
                    </div>
                    {partner.createdAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        Registered:{" "}
                        {partner.createdAt.toDate
                          ? partner.createdAt
                              .toDate()
                              .toLocaleDateString()
                          : "N/A"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  {partner.status === "pending" && (
                    <button
                      onClick={() => handleApprove(partner.id)}
                      disabled={approving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium disabled:opacity-50"
                    >
                      {approving ? "🔄 Approving..." : "✅ Approve"}
                    </button>
                  )}
                  {partner.status === "approved" && (
                    <button
                      onClick={() => handleRevoke(partner.id)}
                      disabled={revoking}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-medium disabled:opacity-50"
                    >
                      {revoking ? "🔄 Revoking..." : "❌ Revoke"}
                    </button>
                  )}
                  {partner.status === "rejected" && (
                    <button
                      onClick={() => handleApprove(partner.id)}
                      disabled={approving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium disabled:opacity-50"
                    >
                      {approving ? "🔄 Approving..." : "✅ Approve Again"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDeliveryPartners;
